import { HOLD_MS, HoldGate } from '../gate/hold-gate'
import { el } from '../dom'
import { COPY } from './copy'

const RING_R = 90
const RING_C = 2 * Math.PI * RING_R

export function createParentGate(opts: { onUnlock: () => void }): {
  root: HTMLElement
  open: () => void
  close: () => void
} {
  const gate = new HoldGate(HOLD_MS)
  let raf = 0

  const progress = document.createElementNS('http://www.w3.org/2000/svg', 'circle')
  progress.setAttribute('class', 'hold-ring__value')
  progress.setAttribute('cx', '100')
  progress.setAttribute('cy', '100')
  progress.setAttribute('r', String(RING_R))
  progress.setAttribute('fill', 'none')
  progress.setAttribute('stroke', 'oklch(0.55 0.165 35)')
  progress.setAttribute('stroke-width', '16')
  progress.setAttribute('stroke-linecap', 'round')
  progress.setAttribute('stroke-dasharray', String(RING_C))
  progress.setAttribute('stroke-dashoffset', String(RING_C))

  const track = document.createElementNS('http://www.w3.org/2000/svg', 'circle')
  track.setAttribute('class', 'hold-ring__track')
  track.setAttribute('cx', '100')
  track.setAttribute('cy', '100')
  track.setAttribute('r', String(RING_R))
  track.setAttribute('fill', 'none')
  track.setAttribute('stroke-width', '16')

  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
  svg.setAttribute('class', 'hold-ring')
  svg.setAttribute('viewBox', '0 0 200 200')
  svg.setAttribute('aria-hidden', 'true')
  svg.append(track, progress)

  const label = el('span', { className: 'hold-circle__label', text: COPY.hold })
  const circle = el(
    'button',
    {
      className: 'hold-circle',
      testId: 'hold-circle',
      attrs: { type: 'button', 'aria-label': COPY.hold },
    },
    [svg, label],
  )

  const card = el('div', { className: 'gate-card', testId: 'parent-gate-card' }, [
    el('h2', { className: 'gate-card__title', text: COPY.parentTitle }),
    el('p', { className: 'gate-card__hint', text: COPY.holdHint }),
    circle,
  ])

  const root = el(
    'div',
    {
      className: 'overlay overlay--gate',
      testId: 'parent-gate',
      attrs: { hidden: '' },
    },
    [card],
  )

  const setProgress = (value: number) => {
    progress.setAttribute('stroke-dashoffset', String(RING_C * (1 - value)))
    circle.style.setProperty('--hold-progress', `${Math.round(value * 100)}%`)
  }

  const stopLoop = () => {
    if (raf) cancelAnimationFrame(raf)
    raf = 0
  }

  const close = () => {
    stopLoop()
    gate.reset()
    setProgress(0)
    root.hidden = true
  }

  const tick = () => {
    const sample = gate.sample(performance.now())
    setProgress(sample.progress)
    if (sample.justUnlocked) {
      stopLoop()
      opts.onUnlock()
      return
    }
    raf = requestAnimationFrame(tick)
  }

  const onDown = (event: PointerEvent) => {
    event.preventDefault()
    circle.setPointerCapture(event.pointerId)
    gate.press(performance.now())
    stopLoop()
    tick()
  }

  const onUp = (event: PointerEvent) => {
    event.preventDefault()
    stopLoop()
    const result = gate.release(performance.now())
    if (result === 'cancelled' || result === 'idle') {
      setProgress(0)
      return
    }
    if (result === 'unlocked') opts.onUnlock()
  }

  circle.addEventListener('pointerdown', onDown)
  circle.addEventListener('pointerup', onUp)
  circle.addEventListener('pointercancel', onUp)

  root.addEventListener('pointerdown', (event) => {
    if (event.target === root) close()
  })

  return {
    root,
    open: () => {
      gate.reset()
      setProgress(0)
      root.hidden = false
    },
    close,
  }
}
